'use client'

import { useEffect, useState } from 'react'
import type { PrescriptionStep } from '@/features/cases'

/** Long enough to read a full step without hurrying — the brand voice is calm, not urgent. */
const TEASER_INTERVAL_MS = 5000

/**
 * Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardHome.tsx`'s `StepTeaser`.
 *
 * One of the advisor's steps at a time, crossfading — a taste of the advice the button underneath
 * opens, so the card previews the work rather than only scoring it.
 *
 * The rotation is visual only. WCAG 2.2.2 (Level A) requires a way to stop content that moves by
 * itself, so this stops dead under `prefers-reduced-motion` and pauses whenever the pointer or
 * keyboard focus is anywhere in the card (`paused`, owned by the caller). Assistive tech reads every
 * line at once from the visually-hidden copy instead of being interrupted on each tick, so the
 * rotation never hides anything from it.
 */
export function StepTeaser({ steps, paused }: { steps: PrescriptionStep[]; paused: boolean }) {
  const [index, setIndex] = useState(0)

  // Any change to the pool restarts the cycle, so a stale index can never point past the array.
  useEffect(() => setIndex(0), [steps.length])

  useEffect(() => {
    if (paused || steps.length < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % steps.length),
      TEASER_INTERVAL_MS,
    )
    return () => window.clearInterval(timer)
  }, [paused, steps.length])

  if (steps.length === 0) return null
  const current = steps[Math.min(index, steps.length - 1)]!

  return (
    // Exactly two lines are reserved (h-9 = 2 x the 18px leading below) so a longer step never nudges
    // the button under it — or the chart beside this card. Centred rather than top-aligned: a step
    // that fits on one line then sits in balanced space instead of leaving a hole above the button.
    <div className="flex h-9 items-center">
      <p
        // Keyed so remounting replays the entry animation on every tick.
        key={current.id}
        aria-hidden="true"
        className="text-muted-foreground animate-in fade-in slide-in-from-bottom-1 line-clamp-2 text-[11px] leading-[18px] duration-500 motion-reduce:animate-none"
      >
        {current.text}
      </p>
      <span className="sr-only">
        Your advisor&apos;s steps: {steps.map((s) => s.text).join(' ')}
      </span>
    </div>
  )
}
